import { uuid } from "@CommonUtils/uuid";
import { Pog } from "./pog";

export interface PlanogramContainerCommonNonCircular {
  uniqueId: string;
  color: string;
  cdts: PlanogramContainerCdts;
  xLeft: number;
  xRight: number;
  yBottom: number;
  yTop: number;
  shelfType: "REGULAR" | "HANGCELL" | "BASKET";
  algoTargetWidth?: number;
  algoMinTargetWidth?: number;
  algoCurrentTargetWidth?: number;
  sales: number;
  minProductHeight: number;
  maxProductHeight: number;
}

export interface PlanogramContainerCdt0 {
  cdt0: string;
}

export interface PlanogramContainerCdt2 {
  cdt0: string;
  cdt1: string;
  cdt2: string;
}

export type PlanogramContainerCdts = (PlanogramContainerCdt0 | PlanogramContainerCdt2)[];

export class PlanogramContainerCommon implements PlanogramContainerCommonNonCircular {
  uniqueId: string;
  color: string;
  cdts: PlanogramContainerCdts;
  xLeft: number;
  xRight: number;
  yBottom: number;
  yTop: number;
  shelfType: "REGULAR" | "HANGCELL" | "BASKET";
  algoTargetWidth?: number | undefined;
  algoMinTargetWidth?: number | undefined;
  algoCurrentTargetWidth?: number | undefined;
  sales: number;
  minProductHeight: number;
  maxProductHeight: number;
  pog: Pog;

  constructor(container: PlanogramContainerCommonNonCircular, pog: Pog) {
    this.uniqueId = container.uniqueId;
    this.color = container.color;
    this.cdts = container.cdts.map((cdt) => ({ ...cdt }));
    this.xLeft = container.xLeft;
    this.xRight = container.xRight;
    this.yBottom = container.yBottom;
    this.yTop = container.yTop;
    this.shelfType = container.shelfType;
    this.algoTargetWidth = container.algoTargetWidth;
    this.algoMinTargetWidth = container.algoMinTargetWidth;
    this.algoCurrentTargetWidth = container.algoCurrentTargetWidth;
    this.sales = container.sales;
    this.minProductHeight = container.minProductHeight;
    this.maxProductHeight = container.maxProductHeight;
    this.pog = pog;
  }

  getWidth = () => {
    return this.xRight - this.xLeft;
  };

  getHeight = () => {
    return this.yTop - this.yBottom;
  };

  getLabel = () => {
    let label = "";

    for (const cdts of this.cdts) {
      label += `${cdts.cdt0}`;

      if ("cdt1" in cdts) {
        label += `, ${cdts.cdt1}`;
      }

      if ("cdt2" in cdts) {
        label += `, ${cdts.cdt2}`;
      }
    }

    return label;
  };
}

export class PlanogramContainer extends PlanogramContainerCommon {
  constructor(container: PlanogramContainerCommonNonCircular, pog: Pog) {
    super(container, pog);
  }

  splitRight = () => {
    const halfWidth = this.getWidth() / 2;
    this.xRight -= Math.floor(halfWidth);

    return this.pog.addContainer(
      new PlanogramContainer(
        {
          uniqueId: uuid(),
          color: this.color,
          cdts: this.cdts.map((cdts) => ({ ...cdts })),
          xLeft: this.xRight,
          xRight: this.xRight + Math.floor(halfWidth),
          yBottom: this.yBottom,
          yTop: this.yTop,
          shelfType: this.shelfType,
          sales: this.sales,
          minProductHeight: this.minProductHeight,
          maxProductHeight: this.maxProductHeight,
          algoTargetWidth: this.algoTargetWidth,
          algoMinTargetWidth: this.algoMinTargetWidth,
          algoCurrentTargetWidth: this.algoCurrentTargetWidth,
        },
        this.pog,
      ),
    );
  };

  splitBottom = () => {
    const halfHeight = Math.floor(this.getHeight() / 2);
    this.yTop -= halfHeight;

    return this.pog.addContainer(
      new PlanogramContainer(
        {
          uniqueId: uuid(),
          color: this.color,
          cdts: this.cdts.map((cdts) => ({ ...cdts })),
          xLeft: this.xLeft,
          xRight: this.xRight,
          yBottom: this.yTop,
          yTop: this.yTop + halfHeight,
          shelfType: this.shelfType,
          sales: this.sales,
          minProductHeight: this.minProductHeight,
          maxProductHeight: this.maxProductHeight,
          algoTargetWidth: this.algoTargetWidth,
          algoMinTargetWidth: this.algoMinTargetWidth,
          algoCurrentTargetWidth: this.algoCurrentTargetWidth,
        },
        this.pog,
      ),
    );
  };

  /**
   * Unrange container. Split containers all will be unranged and the first one will be used for unranged containers.
   */
  unrange = () => {
    this.pog.unrangedContainers.push(new UnrangedPlanogramContainer(this, this.pog));

    const cdtsString = JSON.stringify(this.cdts);
    this.pog.containers = this.pog.containers.filter(
      (container) => JSON.stringify(container.cdts) !== cdtsString,
    );
  };

  /**
   * Delete container fully without ability to restore it.
   * If container is part of split container, only the one split container is deleted now all.
   */
  delete = () => {
    this.pog.containers = this.pog.containers.filter(
      (container) => container.uniqueId !== this.uniqueId,
    );
  };
}

export class UnrangedPlanogramContainer extends PlanogramContainerCommon {
  constructor(container: PlanogramContainerCommonNonCircular, pog: Pog) {
    super(container, pog);
  }

  restore = () => {
    this.pog.unrangedContainers = this.pog.unrangedContainers.filter(
      (container) => container.uniqueId !== this.uniqueId,
    );

    this.pog.containers.push(new PlanogramContainer(this, this.pog));
  };
}
